'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Cookie } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { updateGTMConsent } from '@/components/GoogleTagManager';
import { motion, AnimatePresence } from 'framer-motion';

export default function CookieConsent() {
  const [showConsent, setShowConsent] = useState(false);
  const [cookieAnimation, setCookieAnimation] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = localStorage.getItem('analytics-consent');
    if (!hasConsent) {
      // Show the consent banner after a short delay
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    setCookieAnimation(true);
    setTimeout(() => {
      localStorage.setItem('analytics-consent', 'true');
      
      // Update GTM consent
      updateGTMConsent(true);
      
      setShowConsent(false);
      trackEvent('cookie_consent', { action: 'accepted_all' });
    }, 800);
  };

  const acceptNecessary = () => {
    localStorage.setItem('analytics-consent', 'necessary');
    
    // Update GTM consent to deny analytics
    updateGTMConsent(false);
    
    setShowConsent(false);
    trackEvent('cookie_consent', { action: 'accepted_necessary' });
  };

  if (!showConsent) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 p-5 md:p-6 border border-gray-200 dark:border-gray-700 max-w-5xl mx-auto"
      >
        <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="flex-shrink-0 relative">
            <motion.div
              animate={cookieAnimation ? { 
                rotate: [0, -10, 20, -10, 0],
                scale: [1, 1.2, 0.9, 1.1, 1],
              } : {}}
              transition={{ duration: 0.8 }}
              className="bg-primary/10 p-3 rounded-full"
            >
              <Cookie size={32} className="text-primary" />
            </motion.div>
            {cookieAnimation && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >
                ✓
              </motion.div>
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white flex items-center">
              Cookie Monster Alert! 🍪
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Our site is hungry for cookies (not the chocolate chip kind)! They help us track how you 
              navigate our digital buffet and remember your favorite dishes.
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Choose "Nom Nom All" to let us gobble up all the analytics data, or "Just the Crumbs" if you're on a data diet.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center mt-3 md:mt-0">
            <Button 
              variant="outline" 
              size="lg" 
              onClick={acceptNecessary}
              className="rounded-full px-6 border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Just the Crumbs
            </Button>
            <Button 
              variant="default" 
              size="lg" 
              onClick={acceptAll}
              className="rounded-full px-6 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all"
            >
              Nom Nom All!
            </Button>
          </div>
        </div>
        
        <button 
          onClick={() => setShowConsent(false)} 
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          aria-label="Close cookie consent"
        >
          <X size={20} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
} 